import { useEffect, useState } from 'react'

const useCycleLoaderText = (
  phrases: string[],
  options?: { interval?: number; continuous?: boolean; startDelay?: number }
) => {
  const { interval = 4000, continuous = false, startDelay = 0 } = options || {}
  const [loaderCount, setLoaderCount] = useState(0)
  const [currentText, setCurrentText] = useState(phrases[0])
  const [fade, setFade] = useState('opacity-0')

  const hasLoadedAllText = !continuous && loaderCount >= phrases.length - 1

  useEffect(() => {
    // Don't set up interval if we've loaded all text and not continuous
    if (hasLoadedAllText) {
      return
    }

    let fadeTimeout: NodeJS.Timeout | undefined
    let intervalId: NodeJS.Timeout | undefined

    const updateText = () => {
      const nextIndex = (loaderCount + 1) % phrases.length
      const newPhrase = phrases[nextIndex]

      setFade('opacity-0') // Start fade out
      // Clear any existing fade timeout before creating a new one
      if (fadeTimeout) {
        clearTimeout(fadeTimeout)
      }
      fadeTimeout = setTimeout(() => {
        setCurrentText(newPhrase)
        setFade('opacity-100') // Start fade in after text is updated
        setLoaderCount((prev) => prev + 1)
      }, 500) // Halfway through the interval, change the text
    }

    // Set up initial fade in and start delay
    const initialTimer = setTimeout(() => {
      setFade('opacity-100')

      // Set up the cycling interval after start delay
      intervalId = setInterval(updateText, interval)
    }, startDelay)

    // Clean up timers on component unmount
    return () => {
      clearTimeout(initialTimer)
      if (intervalId) {
        clearInterval(intervalId)
      }
      if (fadeTimeout) {
        clearTimeout(fadeTimeout)
      }
    }
  }, [phrases, loaderCount, interval, continuous, hasLoadedAllText, startDelay])

  return {
    currentText,
    fade,
    hasLoadedAllText,
    className: `transition-opacity duration-1000 ${fade} text-center text-lg`,
  }
}

export default useCycleLoaderText
